import FilterPopoverTabs from '@/Enums/PdfBuilder/FilterPopoverTabs.enum';
import { IClientControlOption, IControlOption, IWhitelabelControlOption } from '@/Interfaces/ControlOption.interface';
import { IFilterFormData } from '@/Interfaces/PdfBuilder/FilterFormData.interface';
import PlatformValues from '@/Interfaces/PdfBuilder/FilterValues/PlatformValues.enum';
import AddFilterButton from '@/components/Filter/AddFilterButton';
import { AdvertisersTabButton, ClientsTabButton, WhitelabelsTabButton } from '@/components/Filter/TabButtons';
import TabFilterPopover from '@/components/Filter/TabFilterPopover';
import TabPanel from '@/components/TabPanel';
import TabSelect from '@/components/TabSelect';
import { areArraysOfObjectsEqual } from '@/helpers/comparators';
import { getFilterOptions } from '@/helpers/filters/filterOptions';
import { isRoleAllowedWhitelabelSelect } from '@/helpers/roles';
import { useAppSelector } from '@/store/hooks';
import { selectMeRole } from '@/store/selectors/me.selectors';
import {
  selectParsedAllClients,
  selectParsedAllWhitelabels,
  selectSharedApiLoading,
} from '@/store/selectors/sharedApi.selectors';
import { a11yProps } from '@/utils/tabProps';
import { Tab, Tabs, tabClasses } from '@mui/material';
import { MouseEvent, SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

interface IProps {
  filters: Partial<IFilterFormData>;
  platforms?: PlatformValues[];
  disabled?: boolean;
}

// Predicate per platform: an advertiser is eligible if it has the integration
// the platform requires. Mirrors `FILTER_API_FILTERS` in
// `helpers/reportingDashboard/index.js` for parity with the Reporting Dashboard.
const PLATFORM_ADVERTISER_PREDICATES: Record<PlatformValues, (record: Record<string, unknown>) => boolean> = {
  [PlatformValues.STV]: record => !!record.beeswaxId,
  [PlatformValues.YouTube]: record => !!record.hasDv360Partner,
  [PlatformValues.Audio]: record => !!record.adswizzId,
  [PlatformValues.DOOH]: record => !!record.neuronId,
  [PlatformValues.Simplifi]: record => !!record.hasSimplifiAccount,
  [PlatformValues.GAM]: record => !!record.hasGamAdvertiser,
};

const BASE_TAB_ID = 'pdf-builder-filter-tab';

const FilterPopover = ({ filters, platforms, disabled }: IProps) => {
  const { setValue } = useFormContext();

  const meRole = useAppSelector(selectMeRole);

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [currentTab, setCurrentTab] = useState(FilterPopoverTabs.Clients);

  const [pendingWhitelabels, setPendingWhitelabels] = useState<IWhitelabelControlOption[]>(filters.whitelabels ?? []);
  const [pendingClients, setPendingClients] = useState<IClientControlOption[]>(filters.clients ?? []);
  const [pendingAdvertisers, setPendingAdvertisers] = useState<IControlOption[]>(filters.advertisers ?? []);

  const clients = useAppSelector(selectParsedAllClients);
  const whitelabels = useAppSelector(selectParsedAllWhitelabels);
  const loadingFilterSource = useAppSelector(selectSharedApiLoading);

  const showWhitelabelLevel = useMemo<boolean>(() => isRoleAllowedWhitelabelSelect(meRole), [meRole]);

  const filtersOpen = useMemo<boolean>(() => Boolean(filterAnchorEl), [filterAnchorEl]);
  const filtersId = useMemo(() => (filtersOpen ? 'filters-popover' : undefined), [filtersOpen]);

  const FILTER_TABS = useMemo(() => {
    const baseTabs = [FilterPopoverTabs.Clients, FilterPopoverTabs.Advertisers];
    return showWhitelabelLevel ? [FilterPopoverTabs.Whitelabels, ...baseTabs] : baseTabs;
  }, [showWhitelabelLevel]);

  const options = useMemo(() => {
    const baseOptions = getFilterOptions({
      userRole: meRole!,
      clients,
      selectedClients: pendingClients,
      selectedWhitelabels: pendingWhitelabels,
      whitelabels,
    });

    // No platform constraint → show every advertiser the user can see.
    if (!platforms?.length) return baseOptions;

    // Union across selected platforms: an advertiser passes if it supports
    // at least one of them. This matches the (a) UX the team agreed on —
    // backend silently no-ops irrelevant platforms, so loose filtering is
    // intentional.
    const predicates = platforms.map(platform => PLATFORM_ADVERTISER_PREDICATES[platform]).filter(Boolean);

    return {
      ...baseOptions,
      advertiser: baseOptions.advertiser.filter(record =>
        predicates.some(predicate => predicate(record as unknown as Record<string, unknown>)),
      ),
    };
  }, [clients, meRole, pendingClients, pendingWhitelabels, platforms, whitelabels]);

  const commonTabPanelProps = useMemo(
    () => ({
      className: 'w-full',
      baseTabId: BASE_TAB_ID,
      value: currentTab,
    }),
    [currentTab],
  );

  const resetPendingValues = useCallback(() => {
    setPendingWhitelabels(filters.whitelabels ?? []);
    setPendingClients(filters.clients ?? []);
    setPendingAdvertisers(filters.advertisers ?? []);
  }, [filters.clients, filters.whitelabels, filters.advertisers]);

  const updateWhitelabelsValue = useCallback(
    (updatedWhitelabels: IWhitelabelControlOption[] = []) => {
      setPendingWhitelabels(updatedWhitelabels);
      setPendingClients([]);
      setPendingAdvertisers([]);
    },
    [setPendingWhitelabels, setPendingClients, setPendingAdvertisers],
  );

  const updateClientsValue = useCallback(
    (updatedClients: IClientControlOption[] = []) => {
      setPendingClients(updatedClients);
      setPendingAdvertisers([]);
    },
    [setPendingClients, setPendingAdvertisers],
  );

  const updateAdvertisersValue = useCallback(
    (updatedAdvertisers: IControlOption[] = []) => {
      setPendingAdvertisers(updatedAdvertisers);
    },
    [setPendingAdvertisers],
  );

  const handleOpen = useCallback(
    ({ currentTarget }: MouseEvent<HTMLButtonElement>) => {
      resetPendingValues();
      setFilterAnchorEl(currentTarget);
    },
    [resetPendingValues],
  );

  const handleClose = useCallback(() => {
    setFilterAnchorEl(null);
    resetPendingValues();
    setCurrentTab(FILTER_TABS[0]);
  }, [FILTER_TABS, resetPendingValues, setCurrentTab, setFilterAnchorEl]);

  const handleSubmit = () => {
    if (
      areArraysOfObjectsEqual(filters.whitelabels, pendingWhitelabels) &&
      areArraysOfObjectsEqual(filters.clients, pendingClients) &&
      areArraysOfObjectsEqual(filters.advertisers, pendingAdvertisers)
    ) {
      handleClose();
      return;
    }
    setValue('whitelabels', pendingWhitelabels);
    setValue('clients', pendingClients);
    setValue('advertisers', pendingAdvertisers);
    handleClose();
  };

  const handleTabChange = useCallback((_: SyntheticEvent, newValue: FilterPopoverTabs) => {
    setCurrentTab(newValue);
  }, []);

  return (
    <>
      <AddFilterButton onClick={handleOpen} describedBy={filtersId} disabled={disabled} />
      <TabFilterPopover
        id={filtersId}
        open={filtersOpen}
        anchorEl={filterAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Tabs
          value={currentTab}
          orientation='vertical'
          onChange={handleTabChange}
          aria-label='pdf builder filter tabs'
          sx={{
            minHeight: 'unset',
            background: '#1B43320A',
            padding: '16px',
            minWidth: '230px',
            [`& .${tabClasses.root}`]: {
              paddingX: 0,
              minHeight: 'unset',
              justifyContent: 'flex-start',
              paddingLeft: '16px',
              paddingRight: '16px',
            },
            [`& .${tabClasses.selected}`]: {
              background: '#1B43321F',
            },
          }}
          slotProps={{
            indicator: {
              hidden: true,
            },
          }}
        >
          {showWhitelabelLevel ? (
            <Tab
              value={FilterPopoverTabs.Whitelabels}
              component={WhitelabelsTabButton}
              {...a11yProps(FilterPopoverTabs.Whitelabels, BASE_TAB_ID)}
            />
          ) : null}
          <Tab
            value={FilterPopoverTabs.Clients}
            component={ClientsTabButton}
            {...a11yProps(FilterPopoverTabs.Clients, BASE_TAB_ID)}
          />
          <Tab
            value={FilterPopoverTabs.Advertisers}
            component={AdvertisersTabButton}
            {...a11yProps(FilterPopoverTabs.Advertisers, BASE_TAB_ID)}
          />
        </Tabs>
        {showWhitelabelLevel ? (
          <TabPanel {...commonTabPanelProps} index={FilterPopoverTabs.Whitelabels}>
            <TabSelect
              label='Whitelabels'
              loading={loadingFilterSource}
              options={options.whitelabel}
              value={pendingWhitelabels}
              onChange={updateWhitelabelsValue}
              onClose={handleClose}
              onSubmit={handleSubmit}
            />
          </TabPanel>
        ) : null}
        <TabPanel {...commonTabPanelProps} index={FilterPopoverTabs.Clients}>
          <TabSelect
            label='Client'
            loading={loadingFilterSource}
            options={options.client}
            value={pendingClients}
            onChange={updateClientsValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </TabPanel>
        <TabPanel {...commonTabPanelProps} index={FilterPopoverTabs.Advertisers}>
          <TabSelect
            label='Advertiser'
            loading={loadingFilterSource}
            options={options.advertiser}
            value={pendingAdvertisers}
            onChange={updateAdvertisersValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </TabPanel>
      </TabFilterPopover>
    </>
  );
};

export default FilterPopover;
